/**
 * ChatArea 组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 * 使用共享 ChatPanel 组件
 */

import { useState } from 'react'
import { useChatStore } from '../store/chatStore'
import { useAuthStore } from '@/shared/store/authStore'
import { memoryAdminApi } from '../services/memory-admin-api'
import { ChatPanel } from '@/shared/components/chat'
import type { Session } from '../types/memory'
import type { ChatMessage, ExtractionBanner, SessionInfo } from '@/shared/components/chat/types'

interface ChatAreaProps {
  projectId: string
  viewingUserId?: string // 正在查看的用户ID（可能是其他用户）
  sessionStatus?: Session['status']
  onReExtract?: () => void
  canReExtract?: boolean // 是否有重新提取权限（developer/admin）
}

export default function ChatArea({ 
  projectId, 
  viewingUserId, 
  sessionStatus,
  onReExtract,
  canReExtract = false,
}: ChatAreaProps) {
  const { user } = useAuthStore()
  const { messages, sessionId, isLoading, sendMessage, error } = useChatStore()

  // 调试日志：检查 store 中的消息
  console.log('🔍 [ChatArea] 渲染 - messages:', JSON.stringify({
    messagesCount: messages.length,
    messages: messages.map(m => ({ id: m.id, role: m.role, contentPreview: m.content?.substring(0, 20) })),
    sessionId,
    isLoading,
    error,
  }, null, 2))

  const [isReExtracting, setIsReExtracting] = useState(false)

  // 用户主键统一用 id
  const currentUserId = user?.id
  
  // 判断是否可以发送消息（只有自己的会话可以发送）
  const canSend = !viewingUserId || viewingUserId === currentUserId
  
  // 是否显示提取失败提示
  const showFailedBanner = (sessionStatus === 'failed' || sessionStatus === 'delayed') && canReExtract

  // 重新提取处理
  const handleReExtract = async () => {
    if (!sessionId || isReExtracting || !currentUserId) return
    
    setIsReExtracting(true)
    try {
      await memoryAdminApi.reExtractSession(sessionId, currentUserId)
      onReExtract?.()
    } catch (err) {
      console.error('Re-extract failed:', err)
    } finally {
      setIsReExtracting(false)
    }
  }

  // 发送消息
  const handleSend = async (message: string) => {
    console.log('📤 [ChatArea] handleSend 被调用:', JSON.stringify({
      message,
      currentUserId,
      userId: user?.id,
      projectId,
      canSend,
      isLoading,
      hasUser: !!user,
    }, null, 2))
    
    if (!user || !currentUserId || isLoading || !canSend) {
      console.log('❌ [ChatArea] handleSend 条件不满足:', JSON.stringify({
        hasUser: !!user,
        hasUserId: !!currentUserId,
        isLoading,
        canSend,
      }, null, 2))
      return
    }
    
    console.log('✅ [ChatArea] 调用 sendMessage:', JSON.stringify({
      userId: currentUserId,
      projectId,
      message,
    }, null, 2))
    await sendMessage(currentUserId, projectId, message)
  }

  // 转换消息格式
  const chatMessages: ChatMessage[] = messages.map(msg => ({
    id: msg.id,
    role: msg.role,
    content: msg.content,
    timestamp: msg.timestamp || new Date().toISOString(),
  }))

  console.log('🔍 [ChatArea] chatMessages 传递给 ChatPanel:', JSON.stringify({
    count: chatMessages.length,
    messages: chatMessages.map(m => ({ id: m.id, role: m.role, contentPreview: m.content?.substring(0, 30) })),
  }, null, 2))

  // 提取失败横幅配置
  const extractionBanner: ExtractionBanner | undefined = showFailedBanner ? {
    status: sessionStatus === 'failed' ? 'failed' : 'delayed',
    message: sessionStatus === 'failed' ? '记忆提取失败' : '延时提取中',
    detail: sessionStatus === 'failed' 
      ? '提取过程发生错误，请点击重新提取' 
      : '正在等待重试，您也可以手动触发',
    onRetry: handleReExtract,
    isRetrying: isReExtracting,
  } : undefined

  // 会话信息配置
  const sessionInfo: SessionInfo | undefined = sessionId ? {
    sessionId,
    userId: viewingUserId || currentUserId,
    messageCount: messages.length,
    isViewingOthers: !!viewingUserId && viewingUserId !== currentUserId,
  } : undefined

  return (
    <ChatPanel
      messages={chatMessages}
      isLoading={isLoading}
      variant="memory"
      theme="dark"
      title="Memory Chat"
      showHeader={true}
      onSend={canSend ? handleSend : undefined}
      placeholder="输入消息，按 Enter 发送..."
      emptyStateTitle="开始对话"
      emptyStateDescription="发送消息开始新会话，AI 将自动提取记忆"
      autoFocus={true}
      extractionBanner={extractionBanner}
      sessionInfo={sessionInfo}
      readOnly={!canSend}
      readOnlyMessage="查看他人会话（只读模式）"
      renderHeader={() => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold bg-gradient-to-br from-blue-500 to-purple-600">
            AI
          </div>
          <div>
            <h2 className="font-semibold flex items-center gap-2 text-foreground">
              Memory Chat
              {sessionInfo?.sessionId && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  !sessionInfo.isViewingOthers
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {!sessionInfo.isViewingOthers ? '我的会话' : '查看他人'}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {sessionInfo?.userId && (
                <span className="text-xs px-2 py-0.5 rounded flex items-center gap-1 bg-blue-500/15 text-blue-400">
                  <span>👤</span>
                  <span className="font-medium">{sessionInfo.userId}</span>
                </span>
              )}
              {sessionInfo?.sessionId && (
                <>
                  <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-xs text-muted-foreground">
                    会话: <span className="text-blue-400">{sessionInfo.sessionId.substring(0, 8)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">|</span>
                    <span className="text-xs text-muted-foreground">
                    消息: <span className="text-foreground">{sessionInfo.messageCount}</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      renderFooter={() => (
        error ? (
          <div className="px-4 py-2 border-t border-border">
            <div className="flex justify-center animate-fade-in">
              <div className="px-4 py-2 rounded-xl text-sm bg-red-500/15 text-red-400">
                {error}
              </div>
            </div>
          </div>
        ) : null
      )}
    />
  )
}
